/**
 * Conversation List Item Component
 * 
 * Displays a single conversation in the sidebar.
 */

import { formatRelativeTime, truncateText, formatMessageCount } from './utils/format-utils.js';
import { t } from '../../i18n.js';
import { Icons } from './utils/icons.js';

/**
 * Create a conversation list item
 * @param {Object} options
 * @param {Object} options.conversation - Conversation object
 * @param {boolean} options.isActive - Whether this conversation is active
 * @param {Function} options.onClick - Callback when conversation is clicked
 * @param {Function} options.onBookmark - Callback to toggle bookmark
 * @param {Function} options.onDelete - Callback to delete conversation
 * @returns {HTMLElement}
 */
export function createConversationListItem({ 
  conversation, 
  isActive = false, 
  onClick, 
  onBookmark, 
  onDelete 
}) {
  const { _id, title, lastMessageAt, messageCount, isBookmarked } = conversation;
  
  const item = document.createElement('div');
  item.className = `ai-conversation-item${isActive ? ' active' : ''}`;
  item.dataset.conversationId = _id;
  
  // Main content (clickable)
  const content = document.createElement('div');
  content.className = 'ai-conversation-content';
  content.onclick = () => {
    if (onClick) onClick(_id);
  };
  
  // Title
  const titleEl = document.createElement('div');
  titleEl.className = 'ai-conversation-title';
  titleEl.textContent = truncateText(title || t('ai_new_conversation'), 40);
  
  // Meta (timestamp and message count)
  const meta = document.createElement('div');
  meta.className = 'ai-conversation-meta';
  
  const time = document.createElement('span');
  time.className = 'ai-conversation-time';
  time.textContent = formatRelativeTime(lastMessageAt);
  
  const count = document.createElement('span');
  count.className = 'ai-conversation-count';
  count.textContent = formatMessageCount(messageCount || 0);
  
  meta.appendChild(time);
  meta.appendChild(document.createTextNode(' · '));
  meta.appendChild(count);
  
  content.appendChild(titleEl);
  content.appendChild(meta);
  
  // Actions (visible on hover)
  const actions = document.createElement('div');
  actions.className = 'ai-conversation-actions';
  
  // Bookmark button
  const bookmarkBtn = document.createElement('button');
  bookmarkBtn.className = `ai-action-btn bookmark${isBookmarked ? ' active' : ''}`;
  bookmarkBtn.title = isBookmarked ? t('ai_remove_bookmark') : t('ai_add_bookmark');
  bookmarkBtn.innerHTML = isBookmarked ? Icons.bookmarkFilled() : Icons.bookmark();
  bookmarkBtn.onclick = (e) => {
    e.stopPropagation();
    if (onBookmark) onBookmark(_id, !isBookmarked);
  };
  
  // Delete button
  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'ai-action-btn delete';
  deleteBtn.title = t('ai_delete_conversation_title');
  deleteBtn.innerHTML = Icons.delete();
  deleteBtn.onclick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(_id);
  };
  
  actions.appendChild(bookmarkBtn);
  actions.appendChild(deleteBtn);
  
  item.appendChild(content);
  item.appendChild(actions);
  
  return item;
}

/**
 * Create a date group header
 */
export function createDateGroupHeader(label) {
  const header = document.createElement('div');
  header.className = 'ai-conversation-group-header';
  header.textContent = label;
  return header;
}
